import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Briefcase, Users, Inbox, GitBranch, Bell, Database, Wallet, FileSignature, Map, ScrollText, LogOut, UserPlus, Trophy, BookOpen, Smartphone, Building2, GraduationCap, Megaphone, Globe2, KeyRound, FileText, MessageCircle, BarChart3, Wrench } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useBadges } from "@/context/BadgeContext";
import { ROLES, isInternal, OPS_ROLES, PERMS, INTERNAL } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { BrandMark } from "@/components/BrandMark";

export const NAV_GROUPS = [
  { label: "Operasional", items: [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, roles: OPS_ROLES },
    { to: "/pipeline", label: "Pipeline Rekrutmen", icon: GitBranch, roles: OPS_ROLES },
    { to: "/queue", label: "Antrian Review", icon: Inbox, roles: PERMS.queue },
    { to: "/cv-maker", label: "CV Maker", icon: FileText, roles: PERMS.cv_maker },
    { to: "/candidates/new", label: "Daftarkan Siswa", icon: UserPlus, roles: ["vendor_leader", "vendor_operator", "satellite_staff"] },
    { to: "/notifications", label: "Notifikasi", icon: Bell },
  ] },
  { label: "Analitik & Laporan", items: [
    { to: "/heatmap", label: "Peta Asal Siswa", icon: Map, roles: OPS_ROLES },
    { to: "/scorecard", label: "Kartu Skor Vendor", icon: Trophy, roles: [...INTERNAL, "vendor_leader", "vendor_operator"] },
  ] },
  { label: "Corporate Communication", items: [
    { to: "/job-orders", label: "Job Order", icon: Briefcase, end: true, roles: OPS_ROLES },
    { to: "/job-orders", search: "?program=magang", label: "Magang / Ikusei Shuro", icon: GraduationCap, sub: true, roles: OPS_ROLES },
    { to: "/job-orders", search: "?program=ssw", label: "SSW", icon: Briefcase, sub: true, roles: OPS_ROLES },
    { to: "/job-orders", search: "?program=pmi_non_jepang", label: "PMI Non Jepang", icon: Globe2, sub: true, roles: OPS_ROLES },
    { to: "/job-orders", search: "?program=engineering", label: "Engineering", icon: Wrench, sub: true, roles: OPS_ROLES },
  ] },
  { label: "HRD", items: [
    { to: "/inbox", label: "Inbox WhatsApp", icon: MessageCircle, end: true, roles: PERMS.inbox },
    { to: "/inbox/analytics", label: "Analisa Customer", icon: BarChart3, sub: true, roles: PERMS.inbox },
    { to: "/candidates", label: "Kandidat", icon: Users, end: true, roles: OPS_ROLES },
    { to: "/candidates", search: "?source=vendor", label: "LPK / Lembaga Rekanan", icon: Building2, sub: true, roles: INTERNAL },
    { to: "/candidates", search: "?source=yutaka_vokasi", label: "Rekrutmen Yutaka Vokasi", icon: GraduationCap, sub: true, roles: INTERNAL },
    { to: "/candidates", search: "?source=social_media", label: "CRM Media Sosial", icon: Megaphone, sub: true, roles: INTERNAL },
  ] },
  { label: "Komersial & Kerja Sama", items: [
    { to: "/commercial", label: "Target & Fee", icon: Wallet, roles: PERMS.money },
    { to: "/agreements", label: "Perjanjian", icon: FileSignature, roles: PERMS.agreements_view },
  ] },
  { label: "Administrasi", items: [
    { to: "/master", label: "Master Data", icon: Database, roles: [...new Set([...PERMS.master, ...PERMS.users, ...PERMS.branding, ...PERMS.whatsapp_config, ...PERMS.vendor_write])] },
    { to: "/alumni", label: "Alumni", icon: GraduationCap, roles: OPS_ROLES },
    { to: "/audit", label: "Audit Log", icon: ScrollText, roles: PERMS.audit },
    { to: "/mobile", label: "Mobile Management", icon: Smartphone, roles: PERMS.mobile },
  ] },
  { label: "Bantuan", items: [
    { to: "/guide", label: "Panduan Sistem", icon: BookOpen },
  ] },
];

export const Sidebar = ({ collapsed, onNavigate }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const badges = useBadges();
  const countFor = (to) => (to === "/queue" ? badges.pending_queue : to === "/notifications" ? badges.unread : to === "/inbox" ? badges.wa_unassigned : 0);
  const groups = NAV_GROUPS.map((g) => ({ ...g, items: g.items.filter((n) => (!n.roles || n.roles.includes(user.role)) && (!n.search || !n.search.startsWith("?source=") || !user.source_scope || n.search === `?source=${user.source_scope}`)) })).filter((g) => g.items.length > 0);
  return (
    <aside data-testid="sidebar" className={cn("flex h-full flex-col bg-[hsl(var(--surface))] border-r border-border transition-[width] duration-300", collapsed ? "w-[72px]" : "w-[260px]")}>
      <div className="flex items-center gap-3 px-4 h-16 border-b border-border">
        <BrandMark className="h-9 w-9" testId="sidebar-brand-mark" />
        {!collapsed && (
          <div className="leading-tight">
            <p className="font-heading font-semibold" data-testid="app-brand-name">Yutaka CRM</p>
            <p className="text-[10px] text-primary italic leading-tight" data-testid="app-brand-tagline">Grow Together be Stronger</p>
            <p className="text-[10px] text-muted-foreground tracking-wider uppercase">{isInternal(user.role) ? "Internal" : "Portal Vendor"}</p>
          </div>
        )}
      </div>
      <nav className="flex-1 overflow-y-auto p-3 space-y-4">
        {groups.map((g, gi) => (
          <div key={g.label} data-testid={`nav-group-${g.label.split(" ")[0].toLowerCase()}`} className={cn(gi > 0 && "pt-4 border-t border-border/80")}>
            {collapsed ? null : <p className="eyebrow px-3 mb-2 text-[10px] font-bold text-foreground/80">{g.label}</p>}
            <div className="space-y-1">
              {g.items.map((n) => {
                const key = n.to + (n.search || "");
                const tid = `nav-${(n.to.replace(/\//g, "") || "dashboard")}${n.search ? `-${n.search.split("=")[1]}` : ""}`;
                const subActive = n.search ? location.pathname === n.to && location.search === n.search : null;
                const hasSub = !n.search && g.items.some((x) => x.to === n.to && x.search);
                return (
                <NavLink key={key} to={{ pathname: n.to, search: n.search || "" }} end={n.end} onClick={onNavigate} data-testid={tid}
                  className={({ isActive }) => cn("nav-item relative", (n.search ? subActive : isActive && !location.search) && "active", collapsed && "justify-center px-0", n.sub && !collapsed && "ml-4 py-1.5 text-[13px]", hasSub && "font-semibold")} title={n.label}>
                  <n.icon className={cn("shrink-0", n.sub ? "h-4 w-4" : "h-[18px] w-[18px]")} />
                  {!collapsed && <span className="flex-1">{n.label}</span>}
                  {!n.search && countFor(n.to) > 0 && <span data-testid={`nav-badge-${n.to.replace(/\//g, "")}`} className={cn("rounded-full bg-primary text-primary-foreground text-[10px] font-bold min-w-[18px] h-[18px] px-1 grid place-items-center", collapsed && "absolute top-0.5 right-1.5")}>{countFor(n.to) > 99 ? "99+" : countFor(n.to)}</span>}
                </NavLink>
              ); })}
            </div>
          </div>
        ))}
      </nav>
      <div className="p-3 border-t border-border">
        {!collapsed && (
          <div className="px-2 pb-2">
            <p className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user.name}</p>
            <p className="text-[11px] text-muted-foreground truncate" data-testid="sidebar-user-role">{ROLES[user.role]}{user.vendor ? ` · ${user.vendor.name}` : ""}</p>
          </div>
        )}
        <NavLink to="/change-password" data-testid="nav-change-password" className={({ isActive }) => cn("nav-item w-full", isActive && "active", collapsed && "justify-center px-0")} title="Ganti Password">
          <KeyRound className="h-[18px] w-[18px]" />{!collapsed && "Ganti Password"}
        </NavLink>
        <button data-testid="logout-button" onClick={async () => { await logout(); navigate("/login"); }} className={cn("nav-item w-full", collapsed && "justify-center px-0")}>
          <LogOut className="h-[18px] w-[18px]" />{!collapsed && "Keluar"}
        </button>
      </div>
    </aside>
  );
};
